import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  EmojiEvents as EmojiEventsIcon,
  PersonSearch as PersonSearchIcon
} from '@mui/icons-material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BigBoardTable from '../screens/BigBoardTable';
import SeguimientoTable from '../screens/SeguimientoTable';

// Composant personnalisé pour le panneau de contenu
function TabPanel({ children, value, index }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      sx={{ p: 0, width: '100%', minWidth: 0, overflowX: 'hidden' }}
    >
      {value === index && children}
    </Box>
  );
}

// Props d'accessibilité pour les onglets
function a11yProps(index) {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
}

const TabsContainerRankSegui = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isChangingTab = useRef(false);
  
  // État pour l'onglet actif
  const [value, setValue] = useState(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab === 'seguimiento') return 1;
    if (urlTab === 'ranking') return 0;
    
    // Si pas de tab dans l'URL, vérifier sessionStorage
    const savedTab = sessionStorage.getItem('scoutingActiveTab');
    if (savedTab === '1') return 1;
    return 0;
  });

  // État pour savoir si les composants ont été montés
  const [rankingMounted, setRankingMounted] = useState(false);
  const [seguimientoMounted, setSeguimientoMounted] = useState(false);

  // Sauvegarder l'onglet actif
  useEffect(() => {
    sessionStorage.setItem('scoutingActiveTab', value.toString());
  }, [value]);

  // Surveiller les changements de searchParams uniquement si on ne change pas d'onglet
  useEffect(() => {
    if (!isChangingTab.current) {
      const tab = searchParams.get('tab');
      
      // Sauvegarder les params actuels pour l'onglet actif
      const params = {};
      searchParams.forEach((val, key) => {
        if (key !== 'tab') {
          params[key] = val;
        }
      });
      
      const storageKey = value === 0 ? 'rankingParams' : 'seguimientoParams';
      sessionStorage.setItem(storageKey, JSON.stringify(params));
    }
  }, [searchParams, value]);

  const handleChange = (event, newValue) => {
    if (value === newValue) return; // Éviter de rechanger si c'est le même onglet
    
    isChangingTab.current = true;
    
    // Sauvegarder les paramètres actuels
    const currentParams = {};
    searchParams.forEach((val, key) => {
      if (key !== 'tab') {
        currentParams[key] = val;
      }
    });
    
    const currentStorageKey = value === 0 ? 'rankingParams' : 'seguimientoParams';
    sessionStorage.setItem(currentStorageKey, JSON.stringify(currentParams));
    
    // Changer d'onglet
    setValue(newValue);
    
    // Créer les nouveaux paramètres
    const newParams = new URLSearchParams();
    newParams.set('tab', newValue === 0 ? 'ranking' : 'seguimiento');
    
    // Restaurer les paramètres sauvegardés pour le nouvel onglet
    const newStorageKey = newValue === 0 ? 'rankingParams' : 'seguimientoParams';
    const savedParams = sessionStorage.getItem(newStorageKey);
    
    if (savedParams) {
      try {
        const params = JSON.parse(savedParams);
        Object.entries(params).forEach(([key, val]) => {
          if (key !== 'tab') { // S'assurer de ne pas écraser le paramètre tab
            newParams.set(key, val);
          }
        });
      } catch (e) {
        console.error('Error parsing saved params:', e);
      }
    }
    
    // Mettre à jour l'URL
    setSearchParams(newParams);
    
    // Réinitialiser le flag après un court délai
    setTimeout(() => {
      isChangingTab.current = false;
    }, 100);
  };

  // Configuration des onglets
  const tabsConfig = [
    {
      label: t('scoutingTabs.rankings'),
      icon: <EmojiEventsIcon />,
      component: BigBoardTable,
      mounted: rankingMounted,
      setMounted: setRankingMounted
    },
    {
      label: t('scoutingTabs.tracking'),
      icon: <PersonSearchIcon />,
      component: SeguimientoTable,
      mounted: seguimientoMounted,
      setMounted: setSeguimientoMounted
    }
  ];

  // Au montage, s'assurer que l'URL reflète l'onglet actif
  useEffect(() => {
    const currentTab = searchParams.get('tab');
    const expectedTab = value === 0 ? 'ranking' : 'seguimiento';
    
    if (currentTab !== expectedTab) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', expectedTab);
      
      // Restaurer les paramètres sauvegardés si disponibles
      const storageKey = value === 0 ? 'rankingParams' : 'seguimientoParams';
      const savedParams = sessionStorage.getItem(storageKey);
      
      if (savedParams) {
        try {
          const params = JSON.parse(savedParams);
          Object.entries(params).forEach(([key, val]) => {
            if (key !== 'tab') {
              newParams.set(key, val);
            }
          });
        } catch (e) {
          console.error('Error parsing saved params:', e);
        }
      }
      
      setSearchParams(newParams);
    }
  }, []);

  return (
    <Box sx={{ width: '100%', height: '100%', minWidth: 0, overflowX: 'hidden' }}>
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label={t('scoutingTabs.ariaLabel')}
          variant={isMobile ? "fullWidth" : "standard"}
          scrollButtons={isMobile ? false : "auto"}
          indicatorColor="primary"
          textColor="primary"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: isMobile ? 56 : 64,
              textTransform: 'none',
              fontSize: isMobile ? '0.875rem' : '1rem',
              fontWeight: 500,
            },
            '& .MuiTabs-indicator': {
              height: 3,
            }
          }}
        >
          {tabsConfig.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition={isMobile ? "top" : "start"}
              {...a11yProps(index)}
              sx={{
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                }
              }}
            />
          ))}
        </Tabs>
      </Paper>

      <Box sx={{ mt: 2, width: '100%', minWidth: 0, overflowX: 'hidden' }}>
        {tabsConfig.map((tab, index) => (
          <TabPanel key={index} value={value} index={index}>
            {(value === index || tab.mounted) && (
              <Box ref={() => tab.setMounted(true)}>
                <tab.component />
              </Box>
            )}
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
};

export default TabsContainerRankSegui;